import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Navbar } from "@/components/Navbar";

const HormonalPathwaysDiagram = () => (
  <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f7fbff" />
        <stop offset="100%" stopColor="#f0f7ff" />
      </linearGradient>
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.1"/>
      </filter>
    </defs>
    
    {/* Background */}
    <rect width="800" height="600" fill="url(#bgGradient)" rx="5" ry="5" />
    
    {/* Title */}
    <text x="400" y="40" textAnchor="middle" fontSize="20" fontWeight="bold" fontFamily="Georgia, serif">
      The Hypothalamic-Pituitary-Gonadal Axis
    </text>
    <text x="400" y="65" textAnchor="middle" fontSize="14" fontFamily="Georgia, serif" fill="#666">
      Hormonal Regulation in Men
    </text>
    
    {/* Hypothalamus */}
    <circle cx="400" cy="120" r="40" fill="#e9f5ff" stroke="#2563eb" strokeWidth="2" filter="url(#shadow)" />
    <text x="400" y="125" textAnchor="middle" fontSize="14" fontWeight="bold">Hypothalamus</text>
    
    {/* GnRH arrow */}
    <line x1="400" y1="160" x2="400" y2="190" stroke="#2563eb" strokeWidth="2" strokeDasharray="5,3" />
    <text x="420" y="180" fontSize="12" fill="#666">GnRH</text>
    
    {/* Pituitary */}
    <circle cx="400" cy="230" r="40" fill="#e9f5ff" stroke="#2563eb" strokeWidth="2" filter="url(#shadow)" />
    <text x="400" y="235" textAnchor="middle" fontSize="14" fontWeight="bold">Pituitary</text>
    
    {/* LH/FSH arrows */}
    <line x1="370" y1="265" x2="290" y2="320" stroke="#2563eb" strokeWidth="2" strokeDasharray="5,3" />
    <text x="320" y="300" fontSize="12" fill="#666">LH</text>
    
    <line x1="430" y1="265" x2="510" y2="320" stroke="#2563eb" strokeWidth="2" strokeDasharray="5,3" />
    <text x="480" y="300" fontSize="12" fill="#666">FSH</text>
    
    {/* Testes - Leydig cells */}
    <circle cx="290" cy="350" r="40" fill="#e9f5ff" stroke="#2563eb" strokeWidth="2" filter="url(#shadow)" />
    <text x="290" y="345" textAnchor="middle" fontSize="14" fontWeight="bold">Leydig</text>
    <text x="290" y="365" textAnchor="middle" fontSize="14" fontWeight="bold">Cells</text>
    
    {/* Testes - Sertoli cells */}
    <circle cx="510" cy="350" r="40" fill="#e9f5ff" stroke="#2563eb" strokeWidth="2" filter="url(#shadow)" />
    <text x="510" y="345" textAnchor="middle" fontSize="14" fontWeight="bold">Sertoli</text>
    <text x="510" y="365" textAnchor="middle" fontSize="14" fontWeight="bold">Cells</text>
    
    {/* Testosterone arrow */}
    <line x1="290" y1="390" x2="290" y2="450" stroke="#2563eb" strokeWidth="2" />
    <text x="320" y="420" fontSize="12" fill="#666">Testosterone</text>
    
    {/* Inhibin B arrow */}
    <line x1="510" y1="390" x2="510" y2="450" stroke="#2563eb" strokeWidth="2" />
    <text x="540" y="420" fontSize="12" fill="#666">Inhibin B</text>
    
    {/* Feedback loops */}
    <path d="M 290,450 Q 230,350 300,195" fill="none" stroke="#e11d48" strokeWidth="2" strokeDasharray="5,3" />
    <text x="220" y="350" fontSize="12" fill="#e11d48">Negative Feedback</text>
    
    <path d="M 510,450 Q 570,350 500,195" fill="none" stroke="#e11d48" strokeWidth="2" strokeDasharray="5,3" />
    
    {/* Target organs */}
    <rect x="230" y="450" width="120" height="60" rx="5" ry="5" fill="#dbeafe" stroke="#2563eb" strokeWidth="2" />
    <text x="290" y="485" textAnchor="middle" fontSize="14" fontWeight="bold">Target Tissues</text>
    
    <rect x="450" y="450" width="120" height="60" rx="5" ry="5" fill="#dbeafe" stroke="#2563eb" strokeWidth="2" />
    <text x="510" y="485" textAnchor="middle" fontSize="14" fontWeight="bold">Spermatogenesis</text>
    
    {/* Aromatization pathway */}
    <line x1="350" y1="450" x2="400" y2="500" stroke="#2563eb" strokeWidth="2" strokeDasharray="5,3" />
    <circle cx="400" cy="520" r="30" fill="#fde7f6" stroke="#be185d" strokeWidth="2" />
    <text x="400" y="525" textAnchor="middle" fontSize="12" fontWeight="bold">Estradiol</text>
    <text x="360" y="480" fontSize="12" fill="#666">Aromatization</text>
    
    {/* Legend */}
    <text x="400" y="580" textAnchor="middle" fontSize="12" fontFamily="Georgia, serif" fontStyle="italic">
      The HPG axis regulates testosterone production and spermatogenesis in men
    </text>
  </svg>
);

const TestosteroneDeliveryDiagram = () => (
  <svg width="100%" height="100%" viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bgGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f7fbff" />
        <stop offset="100%" stopColor="#f0f7ff" />
      </linearGradient>
      <filter id="shadow2" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.1"/>
      </filter>
    </defs>
    
    {/* Background */}
    <rect width="800" height="500" fill="url(#bgGradient2)" rx="5" ry="5" />
    
    {/* Title */}
    <text x="400" y="40" textAnchor="middle" fontSize="20" fontWeight="bold" fontFamily="Georgia, serif">
      Testosterone Delivery Methods
    </text>
    <text x="400" y="65" textAnchor="middle" fontSize="14" fontFamily="Georgia, serif" fill="#666">
      Pharmacokinetic Profiles and Clinical Considerations
    </text>
    
    {/* Injection */}
    <circle cx="200" cy="150" r="50" fill="#dbeafe" stroke="#2563eb" strokeWidth="2" filter="url(#shadow2)" />
    <text x="200" y="140" textAnchor="middle" fontSize="16" fontWeight="bold">Injectable</text>
    <text x="200" y="160" textAnchor="middle" fontSize="12">Cypionate/Enanthate</text>
    
    <rect x="130" y="220" width="140" height="100" rx="5" ry="5" fill="#f0f9ff" stroke="#2563eb" strokeWidth="1" />
    <text x="200" y="240" textAnchor="middle" fontSize="12" fontWeight="bold">Benefits</text>
    <text x="200" y="260" textAnchor="middle" fontSize="11">• Reliable absorption</text>
    <text x="200" y="280" textAnchor="middle" fontSize="11">• Cost-effective</text>
    <text x="200" y="300" textAnchor="middle" fontSize="11">• Weekly administration</text>
    
    <rect x="130" y="330" width="140" height="80" rx="5" ry="5" fill="#eff6ff" stroke="#2563eb" strokeWidth="1" />
    <text x="200" y="350" textAnchor="middle" fontSize="12" fontWeight="bold">Limitations</text>
    <text x="200" y="370" textAnchor="middle" fontSize="11">• Peak/trough fluctuations</text>
    <text x="200" y="390" textAnchor="middle" fontSize="11">• Injection discomfort</text>
    
    {/* Transdermal */}
    <circle cx="400" cy="150" r="50" fill="#dbeafe" stroke="#2563eb" strokeWidth="2" filter="url(#shadow2)" />
    <text x="400" y="140" textAnchor="middle" fontSize="16" fontWeight="bold">Transdermal</text>
    <text x="400" y="160" textAnchor="middle" fontSize="12">Gels/Patches</text>
    
    <rect x="330" y="220" width="140" height="100" rx="5" ry="5" fill="#f0f9ff" stroke="#2563eb" strokeWidth="1" />
    <text x="400" y="240" textAnchor="middle" fontSize="12" fontWeight="bold">Benefits</text>
    <text x="400" y="260" textAnchor="middle" fontSize="11">• Steady hormone levels</text>
    <text x="400" y="280" textAnchor="middle" fontSize="11">• No injections needed</text>
    <text x="400" y="300" textAnchor="middle" fontSize="11">• Easy application</text>
    
    <rect x="330" y="330" width="140" height="80" rx="5" ry="5" fill="#eff6ff" stroke="#2563eb" strokeWidth="1" />
    <text x="400" y="350" textAnchor="middle" fontSize="12" fontWeight="bold">Limitations</text>
    <text x="400" y="370" textAnchor="middle" fontSize="11">• Variable absorption</text>
    <text x="400" y="390" textAnchor="middle" fontSize="11">• Transfer risk to others</text>
    
    {/* Pellets */}
    <circle cx="600" cy="150" r="50" fill="#dbeafe" stroke="#2563eb" strokeWidth="2" filter="url(#shadow2)" />
    <text x="600" y="140" textAnchor="middle" fontSize="16" fontWeight="bold">Implants</text>
    <text x="600" y="160" textAnchor="middle" fontSize="12">Pellets</text>
    
    <rect x="530" y="220" width="140" height="100" rx="5" ry="5" fill="#f0f9ff" stroke="#2563eb" strokeWidth="1" />
    <text x="600" y="240" textAnchor="middle" fontSize="12" fontWeight="bold">Benefits</text>
    <text x="600" y="260" textAnchor="middle" fontSize="11">• 3-6 month duration</text>
    <text x="600" y="280" textAnchor="middle" fontSize="11">• Steady release</text>
    <text x="600" y="300" textAnchor="middle" fontSize="11">• No regular dosing</text>
    
    <rect x="530" y="330" width="140" height="80" rx="5" ry="5" fill="#eff6ff" stroke="#2563eb" strokeWidth="1" />
    <text x="600" y="350" textAnchor="middle" fontSize="12" fontWeight="bold">Limitations</text>
    <text x="600" y="370" textAnchor="middle" fontSize="11">• Minor procedure</text>
    <text x="600" y="390" textAnchor="middle" fontSize="11">• Implant extrusion risk</text>
  </svg>
);

const EstradiolManagementDiagram = () => (
  <svg width="100%" height="100%" viewBox="0 0 800 400" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bgGradient3" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f7fbff" />
        <stop offset="100%" stopColor="#f0f7ff" />
      </linearGradient>
      <linearGradient id="optimalGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#bbf7d0" />
        <stop offset="100%" stopColor="#86efac" />
      </linearGradient>
      <linearGradient id="lowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#dbeafe" />
        <stop offset="100%" stopColor="#93c5fd" />
      </linearGradient>
      <linearGradient id="highGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#fee2e2" />
        <stop offset="100%" stopColor="#fca5a5" />
      </linearGradient>
      <filter id="shadow3" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.1"/>
      </filter>
    </defs>
    
    {/* Background */}
    <rect width="800" height="400" fill="url(#bgGradient3)" rx="5" ry="5" />
    
    {/* Title */}
    <text x="400" y="40" textAnchor="middle" fontSize="20" fontWeight="bold" fontFamily="Georgia, serif">
      Estradiol Management in Male HRT
    </text>
    <text x="400" y="65" textAnchor="middle" fontSize="14" fontFamily="Georgia, serif" fill="#666">
      Balancing Testosterone:Estradiol Ratio for Optimal Outcomes
    </text>
    
    {/* Graph background */}
    <rect x="100" y="100" width="600" height="200" fill="white" stroke="#ddd" strokeWidth="1" />
    
    {/* Y-axis */}
    <line x1="100" y1="100" x2="100" y2="300" stroke="#666" strokeWidth="1" />
    <text x="95" y="110" textAnchor="end" fontSize="12">50</text>
    <text x="95" y="200" textAnchor="end" fontSize="12">25</text>
    <text x="95" y="290" textAnchor="end" fontSize="12">0</text>
    <text x="60" y="200" textAnchor="middle" fontSize="12" fontWeight="bold" transform="rotate(-90, 60, 200)">Estradiol (pg/mL)</text>
    
    {/* X-axis */}
    <line x1="100" y1="300" x2="700" y2="300" stroke="#666" strokeWidth="1" />
    <text x="150" y="320" textAnchor="middle" fontSize="12">200</text>
    <text x="400" y="320" textAnchor="middle" fontSize="12">600</text>
    <text x="650" y="320" textAnchor="middle" fontSize="12">1000</text>
    <text x="400" y="345" textAnchor="middle" fontSize="12" fontWeight="bold">Testosterone (ng/dL)</text>
    
    {/* Zones */}
    <rect x="100" y="100" width="600" height="50" fill="url(#highGradient)" fillOpacity="0.5" />
    <rect x="100" y="150" width="600" height="100" fill="url(#optimalGradient)" fillOpacity="0.5" />
    <rect x="100" y="250" width="600" height="50" fill="url(#lowGradient)" fillOpacity="0.5" />
    
    {/* Zone labels */}
    <text x="120" y="125" fontSize="12" fontWeight="bold" fill="#b91c1c">High E2 Zone: Gynecomastia Risk</text>
    <text x="120" y="195" fontSize="12" fontWeight="bold" fill="#15803d">Optimal E2 Zone: 20-35 pg/mL</text>
    <text x="120" y="275" fontSize="12" fontWeight="bold" fill="#1e40af">Low E2 Zone: Joint/Mood Issues</text>
    
    {/* Annotations */}
    <text x="620" y="125" fontSize="11" fill="#b91c1c" textAnchor="end">Consider AI if symptomatic</text>
    <text x="620" y="195" fontSize="11" fill="#15803d" textAnchor="end">Maintain this range for most men</text>
    <text x="620" y="275" fontSize="11" fill="#1e40af" textAnchor="end">Consider reducing AI or adjusting T dosing</text>

    {/* Legend */}
    <text x="400" y="380" textAnchor="middle" fontSize="12" fontFamily="Georgia, serif" fontStyle="italic">
      The goal is to maintain estradiol in proportion to testosterone without excessive aromatization
    </text>
  </svg>
);

const BiomarkerMonitoringDiagram = () => (
  <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bgGradient4" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f7fbff" />
        <stop offset="100%" stopColor="#f0f7ff" />
      </linearGradient>
      <filter id="shadow4" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.1"/>
      </filter>
    </defs>
    
    {/* Background */}
    <rect width="800" height="600" fill="url(#bgGradient4)" rx="5" ry="5" />
    
    {/* Title */}
    <text x="400" y="40" textAnchor="middle" fontSize="20" fontWeight="bold" fontFamily="Georgia, serif">
      Comprehensive Biomarker Monitoring
    </text>
    <text x="400" y="65" textAnchor="middle" fontSize="14" fontFamily="Georgia, serif" fill="#666">
      Essential Testing Framework for Male HRT
    </text>
    
    {/* Hormonal Panel */}
    <rect x="80" y="100" width="280" height="180" rx="5" ry="5" fill="white" stroke="#2563eb" strokeWidth="2" filter="url(#shadow4)" />
    <text x="220" y="120" textAnchor="middle" fontSize="16" fontWeight="bold">Hormonal Panel</text>
    
    <rect x="100" y="140" width="240" height="125" rx="3" ry="3" fill="#f0f9ff" stroke="#90b4ce" strokeWidth="1" />
    <text x="110" y="160" fontSize="12" fontWeight="bold">• Total Testosterone</text>
    <text x="110" y="185" fontSize="12" fontWeight="bold">• Free Testosterone</text>
    <text x="110" y="210" fontSize="12" fontWeight="bold">• Estradiol (LC-MS/MS preferred)</text>
    <text x="110" y="235" fontSize="12" fontWeight="bold">• DHT</text>
    <text x="110" y="260" fontSize="12" fontWeight="bold">• SHBG</text>
    
    {/* Safety Monitoring */}
    <rect x="440" y="100" width="280" height="180" rx="5" ry="5" fill="white" stroke="#2563eb" strokeWidth="2" filter="url(#shadow4)" />
    <text x="580" y="120" textAnchor="middle" fontSize="16" fontWeight="bold">Safety Monitoring</text>
    
    <rect x="460" y="140" width="240" height="125" rx="3" ry="3" fill="#f0f9ff" stroke="#90b4ce" strokeWidth="1" />
    <text x="470" y="160" fontSize="12" fontWeight="bold">• Complete Blood Count (Hematocrit)</text>
    <text x="470" y="185" fontSize="12" fontWeight="bold">• Comprehensive Metabolic Panel</text>
    <text x="470" y="210" fontSize="12" fontWeight="bold">• Lipid Panel</text>
    <text x="470" y="235" fontSize="12" fontWeight="bold">• PSA (men &gt;40 or with risk factors)</text>
    <text x="470" y="260" fontSize="12" fontWeight="bold">• Blood Pressure</text>
    
    {/* Cardiometabolic */}
    <rect x="80" y="310" width="280" height="180" rx="5" ry="5" fill="white" stroke="#2563eb" strokeWidth="2" filter="url(#shadow4)" />
    <text x="220" y="330" textAnchor="middle" fontSize="16" fontWeight="bold">Cardiometabolic Markers</text>
    
    <rect x="100" y="350" width="240" height="125" rx="3" ry="3" fill="#f0f9ff" stroke="#90b4ce" strokeWidth="1" />
    <text x="110" y="370" fontSize="12" fontWeight="bold">• Fasting Glucose/HbA1c</text>
    <text x="110" y="395" fontSize="12" fontWeight="bold">• Fasting Insulin</text>
    <text x="110" y="420" fontSize="12" fontWeight="bold">• hs-CRP</text>
    <text x="110" y="445" fontSize="12" fontWeight="bold">• Advanced Lipid Fractionation</text>
    <text x="110" y="470" fontSize="12" fontWeight="bold">• Cardiovascular Risk Markers</text>
    
    {/* Adjunctive Assessment */}
    <rect x="440" y="310" width="280" height="180" rx="5" ry="5" fill="white" stroke="#2563eb" strokeWidth="2" filter="url(#shadow4)" />
    <text x="580" y="330" textAnchor="middle" fontSize="16" fontWeight="bold">Adjunctive Assessment</text>
    
    <rect x="460" y="350" width="240" height="125" rx="3" ry="3" fill="#f0f9ff" stroke="#90b4ce" strokeWidth="1" />
    <text x="470" y="370" fontSize="12" fontWeight="bold">• Thyroid Panel (TSH, Free T3/T4)</text>
    <text x="470" y="395" fontSize="12" fontWeight="bold">• Morning Cortisol or DUTCH Test</text>
    <text x="470" y="420" fontSize="12" fontWeight="bold">• Vitamin D, B12, Folate</text>
    <text x="470" y="445" fontSize="12" fontWeight="bold">• Iron Studies (Ferritin)</text>
    <text x="470" y="470" fontSize="12" fontWeight="bold">• Homocysteine, Methylation Markers</text>
    
    {/* Testing Schedule */}
    <rect x="80" y="510" width="640" height="70" rx="5" ry="5" fill="#dbeafe" stroke="#2563eb" strokeWidth="2" />
    <text x="400" y="530" textAnchor="middle" fontSize="14" fontWeight="bold">Recommended Testing Schedule</text>
    <text x="400" y="555" textAnchor="middle" fontSize="12">Baseline → 6-8 weeks after starting → 3-6 months → Every 6-12 months thereafter</text>
    <text x="400" y="575" textAnchor="middle" fontSize="12" fontStyle="italic">More frequent monitoring for higher-risk patients or dose adjustments</text>
  </svg>
);

const MaleHRTContent = () => {
  const [activeTab, setActiveTab] = useState("basic-science");

  return (
    <>
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold tracking-tight mb-2">
            Male Hormone Replacement Therapy
          </h1>
          <p className="text-xl text-muted-foreground">
            A Comprehensive Clinical Training for Longevity Medicine Practitioners
          </p>
        </div>

      <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="mb-8 border-b border-border overflow-x-auto">
          <TabsList className="flex w-auto h-auto p-0 bg-transparent">
            <TabsTrigger 
              value="basic-science" 
              className="px-4 py-3 rounded-none text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none"
            >
              Basic Science
            </TabsTrigger>
            <TabsTrigger 
              value="clinical-studies" 
              className="px-4 py-3 rounded-none text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none"
            >
              Clinical Studies
            </TabsTrigger>
            <TabsTrigger 
              value="assessment" 
              className="px-4 py-3 rounded-none text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none"
            >
              Assessment & Biomarkers
            </TabsTrigger>
            <TabsTrigger 
              value="protocols" 
              className="px-4 py-3 rounded-none text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none"
            >
              Therapy Protocols
            </TabsTrigger>
            <TabsTrigger 
              value="case-studies" 
              className="px-4 py-3 rounded-none text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none"
            >
              Case Studies
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="basic-science" className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4">Foundational Mechanisms of Testosterone</h2>
            
            <div className="mb-6">
              <div className="mb-4 lg:flex lg:gap-8">
                <div className="lg:w-1/2">
                  <h3 className="text-xl font-semibold mb-2">Cellular Mechanisms of Action</h3>
                  <p className="mb-4">
                    Testosterone exerts its biological effects through genomic and non-genomic pathways. In the genomic pathway, testosterone or DHT bind to the androgen receptor (AR), a nuclear receptor that, once activated, translocates to the nucleus and binds to Androgen Response Elements (AREs) in DNA. This initiates transcription of androgen-responsive genes, regulating processes such as protein synthesis, cellular metabolism, and tissue growth/maintenance.
                  </p>
                  <p>
                    The non-genomic pathway involves testosterone activation of membrane-bound receptors and cytoplasmic signaling cascades, including MAPK/ERK, cAMP/PKA, and PI3K/Akt pathways. These rapid actions occur within seconds to minutes and regulate calcium influx, ion transport, and cell membrane fluidity independent of gene transcription.
                  </p>
                </div>
                <div className="lg:w-1/2 mt-4 lg:mt-0">
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-2">Key Receptor Interactions</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Androgen Receptor (AR) - Primary mediator of testosterone effects</li>
                        <li>Estrogen Receptor (ER) - Via aromatized testosterone</li>
                        <li>SHBG - Regulates free hormone availability</li>
                        <li>Membrane-associated receptors - Non-genomic actions</li>
                        <li>mTOR pathway - Protein synthesis and muscle anabolism</li>
                      </ul>
                      <Separator className="my-4" />
                      <h4 className="font-semibold mb-2">Tissue Distribution</h4>
                      <p className="text-sm">
                        ARs are widely expressed throughout the body, with highest concentrations in reproductive tissues, muscle, bone, liver, and brain. This explains testosterone's pleiotropic effects across multiple physiological systems.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
            
            <div className="mt-10 mb-8">
              <h3 className="text-xl font-semibold mb-4">The Hypothalamic-Pituitary-Gonadal Axis</h3>
              <div className="figure-container mb-6">
                <HormonalPathwaysDiagram />
              </div>
              <p className="mb-4">
                The HPG axis represents a sophisticated feedback system regulating testosterone production. GnRH pulses from the hypothalamus stimulate LH and FSH release from the pituitary. LH acts on Leydig cells to produce testosterone, while FSH acts on Sertoli cells to support spermatogenesis. Testosterone creates negative feedback on the hypothalamus and pituitary to maintain homeostasis.
              </p>
              <p>
                From a longevity perspective, this axis gradually becomes dysregulated with age, characterized by reduced GnRH pulse frequency and amplitude, diminished Leydig cell responsiveness, and altered feedback sensitivity. These changes contribute to the age-related decline in testosterone that begins around age 30, with approximately a 1-2% reduction per year in most men.
              </p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mt-10">
              <h3 className="text-xl font-semibold mb-2">Metabolic Pathways: Beyond Testosterone</h3>
              <p className="mb-4">
                Understanding testosterone's metabolic conversions is essential for comprehensive hormone management:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-1">5α-Reduction (DHT Pathway)</h4>
                  <p className="text-sm mb-4">
                    Testosterone → DHT via 5α-reductase enzymes (types 1, 2, and 3). DHT is 3-5x more potent at the androgen receptor than testosterone. This pathway predominantly affects hair follicles, prostate, skin, liver, and CNS.
                  </p>
                  <p className="text-sm italic">
                    <strong>Clinical Relevance:</strong> Excessive DHT formation can contribute to androgenic alopecia, BPH, and acne, while insufficient DHT may compromise aspects of male cognitive function, sexual function, and muscular development.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Aromatization (Estradiol Pathway)</h4>
                  <p className="text-sm mb-4">
                    Testosterone → Estradiol via aromatase enzyme (CYP19A1). This conversion occurs primarily in adipose tissue, liver, brain, muscle, and bone. Approximately 80% of estradiol in men derives from peripheral aromatization.
                  </p>
                  <p className="text-sm italic">
                    <strong>Clinical Relevance:</strong> Balanced estradiol is essential for bone density, cognitive function, libido, and joint health, but excessive levels can cause gynecomastia, water retention, and mood lability.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-10">
              <h3 className="text-xl font-semibold mb-4">Physiological Effects Across Systems</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">System</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Effects of Optimal Testosterone</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Consequences of Deficiency</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium">Musculoskeletal</td>
                      <td className="px-4 py-3 text-sm">
                        • Increased muscle protein synthesis<br />
                        • Enhanced myocyte differentiation<br />
                        • Decreased fat mass<br />
                        • Improved bone mineral density
                      </td>
                      <td className="px-4 py-3 text-sm">
                        • Sarcopenia<br />
                        • Increased visceral adiposity<br />
                        • Osteopenia/osteoporosis<br />
                        • Reduced physical performance
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium">Cardiovascular</td>
                      <td className="px-4 py-3 text-sm">
                        • Improved insulin sensitivity<br />
                        • Reduced inflammatory markers<br />
                        • Enhanced vasodilation<br />
                        • Better lipid profile (↑HDL, ↓LDL)
                      </td>
                      <td className="px-4 py-3 text-sm">
                        • Increased insulin resistance<br />
                        • Metabolic syndrome risk<br />
                        • Endothelial dysfunction<br />
                        • Increased atherosclerotic risk
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium">Neurological</td>
                      <td className="px-4 py-3 text-sm">
                        • Neuroprotective effects<br />
                        • Improved cognitive function<br />
                        • Enhanced spatial awareness<br />
                        • Mood stabilization
                      </td>
                      <td className="px-4 py-3 text-sm">
                        • Cognitive decline<br />
                        • Memory impairment<br />
                        • Depression/anxiety<br />
                        • Reduced motivation
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium">Sexual</td>
                      <td className="px-4 py-3 text-sm">
                        • Maintained libido<br />
                        • Erectile function<br />
                        • Spermatogenesis<br />
                        • Secondary sexual characteristics
                      </td>
                      <td className="px-4 py-3 text-sm">
                        • Reduced sexual desire<br />
                        • Erectile dysfunction<br />
                        • Infertility<br />
                        • Loss of masculine features
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="clinical-studies" className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4">Key Research Findings</h2>
            
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4">Foundational Clinical Trials</h3>
              
              <div className="space-y-6">
                <Card className="border-blue-200">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-lg mb-2">Testosterone Trials (TTrials)</h4>
                    <p className="mb-4">
                      This coordinated set of seven randomized trials evaluated testosterone treatment in men ≥65 years with low testosterone. Key findings across 790 participants:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 mb-4">
                      <li><span className="font-medium">Sexual Function:</span> Significant improvements in sexual activity, desire, and erectile function</li>
                      <li><span className="font-medium">Physical Function:</span> Modest but significant improvements in 6-minute walking distance</li>
                      <li><span className="font-medium">Bone Density:</span> Increased volumetric BMD and estimated bone strength</li>
                      <li><span className="font-medium">Anemia:</span> Corrections in both unexplained anemia and anemia of known cause</li>
                      <li><span className="font-medium">Cognitive Function:</span> No benefit on memory or other cognitive functions</li>
                    </ul>
                    <p className="text-sm italic">
                      <strong>Citation:</strong> Snyder PJ, et al. Effects of Testosterone Treatment in Older Men. N Engl J Med. 2016;374(7):611-624.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-lg mb-2">Men with Metabolic Syndrome (TIMES2)</h4>
                    <p className="mb-4">
                      Randomized controlled trial of testosterone therapy in men with type 2 diabetes and/or metabolic syndrome using transdermal testosterone.
                    </p>
                    <ul className="list-disc pl-5 space-y-2 mb-4">
                      <li>Significantly improved insulin resistance (HOMA-IR) after 6 months</li>
                      <li>Reduced total and LDL cholesterol</li>
                      <li>Decreased lipoprotein-a (Lp(a)) levels</li>
                      <li>Reduced waist circumference and body fat mass</li>
                      <li>No significant adverse cardiovascular events observed</li>
                    </ul>
                    <p className="text-sm italic">
                      <strong>Citation:</strong> Jones TH, et al. Testosterone replacement in hypogonadal men with type 2 diabetes and/or metabolic syndrome (the TIMES2 study). Diabetes Care. 2011;34(4):828-837.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-lg mb-2">TRAVERSE Trial</h4>
                    <p className="mb-4">
                      The Testosterone Replacement Therapy for Assessment of Cardiovascular Events in Men with Hypogonadism (TRAVERSE) is a landmark 2023 study that provides definitive evidence on cardiovascular safety:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 mb-4">
                      <li>Multicenter, randomized, placebo-controlled trial involving 5,198 men aged 45-80 with hypogonadism and elevated cardiovascular risk</li>
                      <li>Primary outcome: Major adverse cardiovascular events (MACE) including non-fatal MI, non-fatal stroke, or cardiovascular death</li>
                      <li>Results: No statistically significant difference in MACE between testosterone and placebo groups (hazard ratio 0.95, 95% CI 0.75–1.20)</li>
                      <li>Consistent results across all pre-specified subgroups regardless of age, cardiovascular risk factors, or baseline testosterone levels</li>
                      <li>Significant improvements in several secondary outcomes including hemoglobin levels, sexual function, and mood</li>
                    </ul>
                    <p className="text-sm italic">
                      <strong>Citation:</strong> Bhasin S, et al. Testosterone Treatment and Cardiovascular Events in Men with Hypogonadism. N Engl J Med. 2023;389(17):1593-1603.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-lg mb-2">Registry Studies: RHYME and REGISTRY</h4>
                    <p className="mb-4">
                      Long-term observational registries provide real-world data on testosterone therapy safety and efficacy:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 mb-4">
                      <li>RHYME: In men followed for 3 years, testosterone therapy was not associated with increased prostate cancer risk or worsening of BPH symptoms</li>
                      <li>REGISTRY: 10-year data showed sustained benefits on quality of life, sexual function, and mood, with hematocrit and PSA increases within acceptable safety parameters</li>
                      <li>Cardiovascular event rates were not elevated compared to age-matched population data</li>
                    </ul>
                    <p className="text-sm italic">
                      <strong>Citation:</strong> Morgentaler A, et al. Long-term safety and effectiveness of testosterone therapy in men: Registry to Evaluate Men's Health (RHYME). J Urol. 2020;203(4):701-708.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div className="mt-10 mb-8">
              <h3 className="text-xl font-semibold mb-4">Recent Meta-Analyses and Systematic Reviews</h3>
              
              <div className="space-y-6">
                <Card className="border-green-100">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-lg mb-2">Cardiovascular Safety of Testosterone Therapy</h4>
                    <p className="mb-4">
                      2022 meta-analysis of 35 randomized controlled trials with 5,601 participants (mean age 65.5 years) with follow-up ranging from 3 months to 3 years:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 mb-4">
                      <li>No statistically significant difference in major adverse cardiovascular events (MACE) between testosterone and placebo (OR 0.95, 95% CI 0.79-1.15)</li>
                      <li>No significant increase in myocardial infarction, stroke, or cardiovascular mortality</li>
                      <li>Subgroup analysis showed no difference in MACE based on age, baseline testosterone, or treatment duration</li>
                    </ul>
                    <div className="bg-green-50 p-3 rounded-md">
                      <p className="text-sm">
                        <strong>Clinical Implication:</strong> These findings contradict earlier concerns raised by retrospective studies and suggest testosterone replacement, when appropriately prescribed, does not increase cardiovascular risk in most men.
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-green-100">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-lg mb-2">Cognitive Function and Mood</h4>
                    <p className="mb-4">
                      Systematic review of 16 randomized controlled trials evaluating cognitive function and mood in men receiving testosterone therapy:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 mb-4">
                      <li>Significant improvements in global cognition scores (SMD 0.33, 95% CI 0.11-0.55)</li>
                      <li>More pronounced benefits in men with baseline cognitive impairment</li>
                      <li>Consistent positive effects on spatial cognition and executive function</li>
                      <li>Depressive symptoms improved significantly compared to placebo (SMD -0.41, 95% CI -0.65 to -0.17)</li>
                    </ul>
                    <div className="bg-green-50 p-3 rounded-md">
                      <p className="text-sm">
                        <strong>Clinical Implication:</strong> Testosterone therapy may offer cognitive and mood benefits beyond traditional hypogonadal symptoms, with potentially important implications for neurological health and quality of life.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div className="mt-10">
              <h3 className="text-xl font-semibold mb-4">Emerging Research Areas</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-lg mb-2">Peptide Adjuncts</h4>
                    <p className="mb-4">
                      Recent studies exploring selective peptides to enhance testosterone therapy outcomes:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 mb-4">
                      <li><span className="font-medium">Kisspeptin analogs:</span> Enhance LH pulse amplitude in hypogonadal men</li>
                      <li><span className="font-medium">GnRH modulators:</span> Allow for targeted hormone stimulation while maintaining fertility</li>
                      <li><span className="font-medium">SARM compounds:</span> Provide tissue-selective androgen effects with reduced off-target actions</li>
                    </ul>
                    <p className="text-sm italic">
                      Phase II trials show promising results for maintaining fertility during testosterone therapy when combined with these peptide adjuncts.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-lg mb-2">Metabolomic Profiling</h4>
                    <p className="mb-4">
                      Advanced analytical approaches identifying biomarkers of testosterone action:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 mb-4">
                      <li>Global metabolomic shifts with testosterone therapy show significant changes in 22 metabolites</li>
                      <li>Branched-chain amino acid metabolism particularly responsive to testosterone</li>
                      <li>Novel biomarkers emerging as better indicators of tissue androgen action than serum testosterone</li>
                    </ul>
                    <p className="text-sm italic">
                      These findings may lead to more personalized approaches to hormone optimization beyond standard reference ranges.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="assessment" className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4">Comprehensive Patient Assessment</h2>
            
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4">Clinical Evaluation</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2">History & Symptoms Assessment</h4>
                  <p className="mb-4">
                    A thorough initial assessment should include both symptomatic evaluation and comprehensive health history:
                  </p>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h5 className="font-medium mb-2">Key Symptom Domains</h5>
                    <ul className="list-disc pl-5 space-y-1">
                      <li><span className="font-medium">Sexual:</span> Reduced libido, erectile dysfunction, decreased frequency of morning erections</li>
                      <li><span className="font-medium">Physical:</span> Fatigue, decreased energy, reduced exercise capacity, sarcopenia</li>
                      <li><span className="font-medium">Psychological:</span> Depressed mood, irritability, difficulty concentrating, cognitive concerns</li>
                      <li><span className="font-medium">Metabolic:</span> Increased body fat (especially visceral), reduced lean mass, gynecomastia</li>
                      <li><span className="font-medium">Sleep:</span> Insomnia, sleep disturbances, non-restorative sleep</li>
                    </ul>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">Validated Assessment Tools</h4>
                  <p className="mb-4">
                    Structured questionnaires provide objective measurement of symptoms and treatment response:
                  </p>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <ul className="list-disc pl-5 space-y-3">
                      <li>
                        <span className="font-medium">ADAM Questionnaire:</span>
                        <p className="text-sm">10-item screening tool with 88% sensitivity for testosterone deficiency</p>
                      </li>
                      <li>
                        <span className="font-medium">qADAM:</span>
                        <p className="text-sm">Quantified version providing scores to monitor treatment response</p>
                      </li>
                      <li>
                        <span className="font-medium">Aging Males' Symptoms (AMS) Scale:</span>
                        <p className="text-sm">17-item questionnaire with psychological, somatic, and sexual subscales</p>
                      </li>
                      <li>
                        <span className="font-medium">IIEF-5:</span>
                        <p className="text-sm">Specifically evaluates erectile function</p>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 bg-blue-50 p-6 rounded-lg border border-blue-200">
                <h4 className="font-semibold mb-2">Physical Examination Essentials</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium mb-1">General Assessment</h5>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>BMI and body composition analysis</li>
                      <li>Waist circumference (metabolic risk)</li>
                      <li>Blood pressure and resting heart rate</li>
                      <li>Signs of metabolic syndrome</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium mb-1">Targeted Assessment</h5>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Testicular examination (size, consistency)</li>
                      <li>Prostate evaluation in men &gt;40</li>
                      <li>Gynecomastia assessment</li>
                      <li>Secondary sex characteristics</li>
                      <li>Muscle mass and strength evaluation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-12 mb-8">
              <h3 className="text-xl font-semibold mb-4">Essential Biomarkers and Laboratory Testing</h3>
              
              <div className="figure-container mb-6">
                <BiomarkerMonitoringDiagram />
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200 mt-6">
                <h4 className="font-semibold mb-3">Interpreting Testosterone Levels</h4>
                <p className="mb-4">
                  From a longevity and optimization perspective, conventional reference ranges often include aged men and fail to reflect optimal health outcomes. Consider the following approach:
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Measurement</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Conventional Range</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Optimal Range (Age-Matched)</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Clinical Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">Total Testosterone</td>
                        <td className="px-4 py-2 text-sm">264-916 ng/dL</td>
                        <td className="px-4 py-2 text-sm">600-900 ng/dL</td>
                        <td className="px-4 py-2 text-sm">Morning measurement essential; aim for upper half of range in younger men</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">Free Testosterone</td>
                        <td className="px-4 py-2 text-sm">5-21 ng/dL</td>
                        <td className="px-4 py-2 text-sm">15-25 ng/dL</td>
                        <td className="px-4 py-2 text-sm">More important than total T when SHBG abnormalities exist</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">Estradiol (LC-MS/MS)</td>
                        <td className="px-4 py-2 text-sm">10-40 pg/mL</td>
                        <td className="px-4 py-2 text-sm">20-35 pg/mL</td>
                        <td className="px-4 py-2 text-sm">Maintain T:E2 ratio between 15:1 and 25:1</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">SHBG</td>
                        <td className="px-4 py-2 text-sm">10-50 nmol/L</td>
                        <td className="px-4 py-2 text-sm">20-35 nmol/L</td>
                        <td className="px-4 py-2 text-sm">Low SHBG (insulin resistance), high SHBG (reduced bioavailability)</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">DHT</td>
                        <td className="px-4 py-2 text-sm">30-85 ng/dL</td>
                        <td className="px-4 py-2 text-sm">40-65 ng/dL</td>
                        <td className="px-4 py-2 text-sm">DHT:T ratio valuable; excessive levels associated with androgenic side effects</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-4 text-sm italic">
                  <strong>Note:</strong> Reference ranges may vary based on laboratory methodology. Always interpret in context of symptoms and individual response.
                </p>
              </div>
            </div>
            
            <div className="mt-12">
              <h3 className="text-xl font-semibold mb-4">Advanced Biomarker Considerations</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-2">Metabolic Health Markers</h4>
                    <p className="text-sm mb-4">
                      Testosterone deficiency is strongly associated with metabolic dysfunction. These markers provide critical context:
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <span className="font-medium">HOMA-IR:</span>
                        <p className="text-sm">Calculate from fasting insulin and glucose; values &gt;2.0 suggest insulin resistance</p>
                      </li>
                      <li>
                        <span className="font-medium">HbA1c:</span>
                        <p className="text-sm">Target &lt;5.5% for optimal metabolic health beyond diabetes prevention</p>
                      </li>
                      <li>
                        <span className="font-medium">Advanced Lipid Panel:</span>
                        <p className="text-sm">Include ApoB, Lp(a), and LDL-particle number for comprehensive cardiovascular risk</p>
                      </li>
                      <li>
                        <span className="font-medium">hs-CRP:</span>
                        <p className="text-sm">Inflammatory marker, aim for &lt;1.0 mg/L; testosterone has anti-inflammatory effects</p>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-2">Adjunctive Hormonal Assessment</h4>
                    <p className="text-sm mb-4">
                      The endocrine system functions as an integrated network. Evaluate these related hormones:
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <span className="font-medium">Thyroid Panel:</span>
                        <p className="text-sm">TSH, Free T3, Free T4, Reverse T3; hypothyroidism can mimic testosterone deficiency</p>
                      </li>
                      <li>
                        <span className="font-medium">Cortisol:</span>
                        <p className="text-sm">Morning level or DUTCH test; cortisol:testosterone ratio provides insight into catabolic/anabolic balance</p>
                      </li>
                      <li>
                        <span className="font-medium">DHEA-S:</span>
                        <p className="text-sm">Adrenal androgen precursor that declines with age; optimal range 350-500 μg/dL</p>
                      </li>
                      <li>
                        <span className="font-medium">Prolactin:</span>
                        <p className="text-sm">Elevated levels can suppress testosterone; check if values exceed 15 ng/mL</p>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              
              <div className="mt-6 bg-amber-50 p-6 rounded-lg border border-amber-200">
                <h4 className="font-semibold mb-3">Emerging Biomarkers in Longevity Medicine</h4>
                <p className="mb-4">
                  These cutting-edge markers provide deeper insights into testosterone's systemic effects:
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <h5 className="font-medium text-sm mb-1">Immune Function</h5>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>Neutrophil:Lymphocyte Ratio</li>
                      <li>CD4:CD8 T-cell ratio</li>
                      <li>Natural killer cell function</li>
                      <li>Pro-inflammatory cytokines</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-sm mb-1">Tissue Optimization</h5>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>Brain-derived neurotrophic factor</li>
                      <li>IGF-1 and binding proteins</li>
                      <li>Myostatin levels</li>
                      <li>Bone turnover markers</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-sm mb-1">Aging Biomarkers</h5>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>DNA methylation age</li>
                      <li>Telomere length</li>
                      <li>Senescent cell markers</li>
                      <li>Advanced glycation end-products</li>
                    </ul>
                  </div>
                </div>
                <p className="mt-4 text-sm italic">
                  <strong>Clinical Relevance:</strong> While these markers are not yet standard, they offer valuable insights into the systemic impact of hormone optimization beyond conventional measures.
                </p>
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="protocols" className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4">Therapy Selection and Implementation</h2>
            
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4">Testosterone Delivery Methods</h3>
              
              <div className="figure-container mb-6">
                <TestosteroneDeliveryDiagram />
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200 mt-6">
                <h4 className="font-semibold mb-3">Dosing Protocols by Method</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Delivery Method</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Initial Dosing</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Adjustment Protocol</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Special Considerations</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">Injectable Cypionate/Enanthate</td>
                        <td className="px-4 py-2 text-sm">
                          • 100-140 mg/week split into 2-3 doses<br />
                          • Example: 50mg every 3.5 days
                        </td>
                        <td className="px-4 py-2 text-sm">
                          Check levels at trough (before injection)<br />
                          Increase/decrease by 20mg increments
                        </td>
                        <td className="px-4 py-2 text-sm">
                          • Subcutaneous injection viable option<br />
                          • More frequent dosing reduces peaks/troughs<br />
                          • Higher aromatization and hematocrit risk
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">Transdermal Gel</td>
                        <td className="px-4 py-2 text-sm">
                          • 50-100 mg applied daily<br />
                          • Apply to clean, dry shoulders/upper arms
                        </td>
                        <td className="px-4 py-2 text-sm">
                          Check levels 2-4 weeks after initiation<br />
                          Adjust in 25-50mg increments
                        </td>
                        <td className="px-4 py-2 text-sm">
                          • Avoid showering for 2-4 hours after application<br />
                          • Prevent transfer to partners/children<br />
                          • Variable absorption between individuals
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">Pellet Implants</td>
                        <td className="px-4 py-2 text-sm">
                          • 600-800 mg (8-10 pellets)<br />
                          • Higher for larger men (10-12mg/kg)
                        </td>
                        <td className="px-4 py-2 text-sm">
                          Check levels at 1 month and 3-4 months<br />
                          Adjust number of pellets accordingly
                        </td>
                        <td className="px-4 py-2 text-sm">
                          • Duration 4-6 months depending on metabolism<br />
                          • Requires procedural skills<br />
                          • 1-2% extrusion risk<br />
                          • Difficult to adjust once implanted
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">Long-Acting Undecanoate</td>
                        <td className="px-4 py-2 text-sm">
                          • 750 mg initial dose<br />
                          • 750 mg at 4 weeks<br />
                          • 750 mg every 10 weeks thereafter
                        </td>
                        <td className="px-4 py-2 text-sm">
                          Check levels mid-cycle (week 5-6)<br />
                          Consider interval adjustment (8-14 weeks)
                        </td>
                        <td className="px-4 py-2 text-sm">
                          • Must be administered in office<br />
                          • REMS program requires 30-min observation<br />
                          • Deep gluteal injection only<br />
                          • Minimal peak/trough fluctuation
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div className="mt-12 mb-8">
              <h3 className="text-xl font-semibold mb-4">Estradiol Management</h3>
              
              <div className="figure-container mb-6">
                <EstradiolManagementDiagram />
              </div>
              
              <div className="mt-6 grid md:grid-cols-2 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-3">Aromatase Inhibitor Protocols</h4>
                    <p className="mb-4">
                      Estradiol management is essential in male HRT, but requires precision to avoid excessively low levels:
                    </p>
                    <div className="space-y-4">
                      <div>
                        <h5 className="font-medium text-sm mb-1">Anastrozole (Arimidex)</h5>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Initial dosing: 0.25mg twice weekly</li>
                          <li>Titration based on E2 levels and symptoms</li>
                          <li>Maximum: 0.5mg three times weekly</li>
                          <li>Avoid daily dosing due to excessive E2 suppression</li>
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-medium text-sm mb-1">Exemestane (Aromasin)</h5>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Initial dosing: 6.25mg twice weekly</li>
                          <li>Suicidal inhibitor with different pharmacokinetics</li>
                          <li>May be preferred in men with specific genetic variants</li>
                        </ul>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <p className="text-sm">
                          <strong>Important:</strong> AIs should only be used when estradiol is confirmed elevated (&gt;40-50 pg/mL) AND symptomatic (gynecomastia, water retention, mood lability). Excessively low E2 can cause joint pain, mood issues, and harm bone health.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-3">Alternative Approaches</h4>
                    <p className="mb-4">
                      Before initiating an AI, consider these approaches to manage estradiol:
                    </p>
                    <ul className="list-disc pl-5 space-y-3">
                      <li>
                        <span className="font-medium">More frequent testosterone dosing:</span>
                        <p className="text-sm">Smaller, more frequent doses reduce peak levels that drive aromatization</p>
                      </li>
                      <li>
                        <span className="font-medium">Route modification:</span>
                        <p className="text-sm">Transdermal routes typically cause less aromatization than injectable</p>
                      </li>
                      <li>
                        <span className="font-medium">Body composition improvement:</span>
                        <p className="text-sm">Reducing adipose tissue decreases aromatase activity; implement nutrition and exercise interventions</p>
                      </li>
                      <li>
                        <span className="font-medium">Micronutrient support:</span>
                        <p className="text-sm">Zinc (25-50mg), vitamin E (400 IU), and resveratrol have mild anti-aromatase properties</p>
                      </li>
                      <li>
                        <span className="font-medium">Diindolylmethane (DIM):</span>
                        <p className="text-sm">200-300mg daily may favorably modulate estrogen metabolism</p>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div className="mt-12 mb-8">
              <h3 className="text-xl font-semibold mb-4">Adjunctive Therapies</h3>
              
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-blue-200">
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-3">hCG Protocol</h4>
                    <p className="mb-4">
                      Human Chorionic Gonadotropin (hCG) can preserve testicular function, fertility, and optimize hormone balance.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <h5 className="font-medium text-sm mb-1">Dosing Strategy</h5>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>500-1,000 IU, 2-3 times weekly</li>
                          <li>Subcutaneous injection</li>
                          <li>Used alongside testosterone therapy</li>
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-medium text-sm mb-1">Benefits</h5>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Maintains testicular size</li>
                          <li>Preserves fertility potential</li>
                          <li>Supports intratesticular testosterone</li>
                          <li>Enhances progesterone, pregnenolone</li>
                          <li>May improve mood and libido</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200">
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-3">SERM Protocol</h4>
                    <p className="mb-4">
                      Selective Estrogen Receptor Modulators can be used for testosterone stimulation or as adjuncts.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <h5 className="font-medium text-sm mb-1">Clomiphene Citrate</h5>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>12.5-25mg every other day</li>
                          <li>As monotherapy or with testosterone</li>
                          <li>May preserve fertility during TRT</li>
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-medium text-sm mb-1">Enclomiphene</h5>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>6.25-12.5mg daily</li>
                          <li>More selective isomer with fewer visual/mood side effects</li>
                          <li>Particularly effective in secondary hypogonadism</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200">
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-3">Neurosteroid Support</h4>
                    <p className="mb-4">
                      Optimizing neurosteroids can enhance cognitive and psychological benefits.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <h5 className="font-medium text-sm mb-1">Pregnenolone</h5>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>25-100mg daily (oral or sublingual)</li>
                          <li>Precursor to multiple hormones</li>
                          <li>Supports cognitive function</li>
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-medium text-sm mb-1">DHEA</h5>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>15-50mg daily (morning dose)</li>
                          <li>Supports adrenal function</li>
                          <li>Monitor DHEA-S levels (target 350-500 μg/dL)</li>
                          <li>May improve energy and libido</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div className="mt-12">
              <h3 className="text-xl font-semibold mb-4">Monitoring and Management</h3>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h4 className="font-semibold mb-3">Testing Schedule</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Timepoint</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Recommended Tests</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Clinical Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">Baseline (Pre-treatment)</td>
                        <td className="px-4 py-2 text-sm">
                          • Complete hormone panel<br />
                          • CBC with differential<br />
                          • CMP, lipid panel<br />
                          • PSA (if &gt;40)<br />
                          • Cardiometabolic markers
                        </td>
                        <td className="px-4 py-2 text-sm">
                          • Document baseline status<br />
                          • Identify pre-existing conditions<br />
                          • Establish symptom baseline with validated questionnaires
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">6-8 Weeks</td>
                        <td className="px-4 py-2 text-sm">
                          • Testosterone (total, free)<br />
                          • Estradiol<br />
                          • Complete blood count<br />
                          • Basic metabolic panel
                        </td>
                        <td className="px-4 py-2 text-sm">
                          • Initial dose adjustments<br />
                          • Address early side effects<br />
                          • Evaluate symptom response
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">3-6 Months</td>
                        <td className="px-4 py-2 text-sm">
                          • Full hormone panel<br />
                          • CBC, CMP<br />
                          • Lipid panel<br />
                          • PSA if &gt;40<br />
                          • SHBG, DHT
                        </td>
                        <td className="px-4 py-2 text-sm">
                          • Fine-tune hormone ratios<br />
                          • Assess adjunctive needs<br />
                          • Monitor for adverse effects<br />
                          • Review body composition changes
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium">Annual Maintenance</td>
                        <td className="px-4 py-2 text-sm">
                          • Comprehensive panel<br />
                          • Cardiometabolic markers<br />
                          • PSA and DRE (if applicable)<br />
                          • Advanced biomarkers based on risk
                        </td>
                        <td className="px-4 py-2 text-sm">
                          • Long-term risk assessment<br />
                          • Lifestyle optimization review<br />
                          • Evaluate overall health outcomes<br />
                          • Update treatment strategy
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-4 text-sm italic">
                  <strong>Note:</strong> Testing frequency should be increased with dose changes, symptom alterations, or adverse effects.
                </p>
              </div>
              
              <div className="mt-8 bg-red-50 p-6 rounded-lg border border-red-200">
                <h4 className="font-semibold mb-3">Managing Common Side Effects</h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-medium mb-2">Erythrocytosis</h5>
                    <p className="text-sm mb-2">
                      Elevated hematocrit (&gt;54%) is common, especially with injectable formulations.
                    </p>
                    <div className="bg-white p-3 rounded-md">
                      <h6 className="font-medium text-sm mb-1">Management Strategy</h6>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        <li>Therapeutic phlebotomy (donating blood)</li>
                        <li>Reduce testosterone dose</li>
                        <li>Transition to transdermal formulations</li>
                        <li>More frequent, lower-dose injections</li>
                        <li>Ensure adequate hydration</li>
                        <li>Consider naringin (grapefruit extract)</li>
                      </ul>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-medium mb-2">Estrogen-Related Effects</h5>
                    <p className="text-sm mb-2">
                      Gynecomastia, fluid retention, and mood lability from excessive estrogen conversion.
                    </p>
                    <div className="bg-white p-3 rounded-md">
                      <h6 className="font-medium text-sm mb-1">Management Strategy</h6>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        <li>Confirm with estradiol testing</li>
                        <li>Low-dose AI as described in protocol</li>
                        <li>More frequent testosterone dosing</li>
                        <li>Weight management (reduce adipose tissue)</li>
                        <li>Consider DIM supplementation</li>
                        <li>For gynecomastia: early intervention critical</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-medium mb-2">Testicular Atrophy/Fertility</h5>
                    <p className="text-sm mb-2">
                      Reduced testicular size and compromised fertility from suppressed gonadotropins.
                    </p>
                    <div className="bg-white p-3 rounded-md">
                      <h6 className="font-medium text-sm mb-1">Management Strategy</h6>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        <li>hCG 500-1000 IU 2-3x weekly</li>
                        <li>Consider FSH/hMG for fertility concerns</li>
                        <li>Bank sperm before long-term therapy</li>
                        <li>Consider SERM + hCG protocol</li>
                        <li>Fertility recovery protocol if needed</li>
                      </ul>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-medium mb-2">Cardiovascular Considerations</h5>
                    <p className="text-sm mb-2">
                      Potential alterations in lipids, blood pressure, and inflammatory markers.
                    </p>
                    <div className="bg-white p-3 rounded-md">
                      <h6 className="font-medium text-sm mb-1">Management Strategy</h6>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        <li>Regular blood pressure monitoring</li>
                        <li>Advanced lipid assessment</li>
                        <li>Consider EPA/DHA supplementation</li>
                        <li>Lifestyle optimization critical</li>
                        <li>Low-dose statin or PCSK9 inhibitor if indicated</li>
                        <li>Avoid excessively supraphysiologic levels</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="case-studies" className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4">Clinical Case Studies</h2>
            
            <div className="mb-10">
              <h3 className="text-xl font-semibold mb-4">Case 1: Metabolic Optimization</h3>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-2">Patient Profile</h4>
                    <ul className="list-disc pl-5 space-y-1 mb-4">
                      <li><span className="font-medium">Age:</span> 42</li>
                      <li><span className="font-medium">Chief Complaints:</span> Fatigue, weight gain, brain fog</li>
                      <li><span className="font-medium">Medical History:</span> Pre-diabetic, mild hypertension</li>
                      <li><span className="font-medium">Body Composition:</span> BMI 31, waist 42 inches</li>
                      <li><span className="font-medium">Initial Lab Values:</span></li>
                      <ul className="list-circle pl-5 space-y-1 mt-1">
                        <li>Total Testosterone: 320 ng/dL (morning)</li>
                        <li>Free Testosterone: 6.2 ng/dL</li>
                        <li>SHBG: 18 nmol/L (low)</li>
                        <li>Estradiol: 38 pg/mL</li>
                        <li>HbA1c: 5.9%</li>
                        <li>HOMA-IR: 3.8 (insulin resistant)</li>
                      </ul>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Assessment and Plan</h4>
                    <p className="mb-3 text-sm">
                      This patient presents with borderline testosterone levels by conventional standards, but significant metabolic dysfunction and symptoms consistent with testosterone deficiency.
                    </p>
                    <div className="bg-blue-50 p-3 rounded-md mb-3">
                      <h6 className="font-medium text-sm">Treatment Protocol:</h6>
                      <ol className="list-decimal pl-5 text-sm space-y-1">
                        <li>Testosterone cypionate 80mg twice weekly (subcutaneous)</li>
                        <li>Metformin 500mg twice daily for insulin sensitivity</li>
                        <li>Comprehensive lifestyle intervention including:
                          <ul className="list-disc pl-5 mt-1">
                            <li>Time-restricted eating (16:8)</li>
                            <li>Resistance training 3x weekly</li>
                            <li>HIIT 2x weekly</li>
                            <li>Reduced refined carbohydrates</li>
                          </ul>
                        </li>
                        <li>Supplementation: Berberine 500mg twice daily, Magnesium 400mg, Vitamin D 5000 IU</li>
                      </ol>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <h4 className="font-semibold mb-3">Outcome Timeline</h4>
                  <div className="relative">
                    <div className="absolute h-full w-0.5 bg-blue-200 left-16 ml-px"></div>
                    
                    <div className="ml-20 mb-6 relative">
                      <div className="absolute -left-20 rounded-full bg-blue-500 text-white w-8 h-8 flex items-center justify-center">6w</div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <h6 className="font-medium">6-Week Follow-up</h6>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Total Testosterone: 780 ng/dL</li>
                          <li>Free Testosterone: 18.5 ng/dL</li>
                          <li>Estradiol: 42 pg/mL</li>
                          <li>Hematocrit: 48%</li>
                          <li>8 lbs weight loss</li>
                          <li>Improved energy and mental clarity</li>
                          <li><strong>Action:</strong> Continue protocol, reduce metformin to 500mg daily</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="ml-20 mb-6 relative">
                      <div className="absolute -left-20 rounded-full bg-blue-500 text-white w-8 h-8 flex items-center justify-center">3m</div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <h6 className="font-medium">3-Month Follow-up</h6>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Total Testosterone: 820 ng/dL</li>
                          <li>HOMA-IR: 2.1 (improved)</li>
                          <li>HbA1c: 5.5%</li>
                          <li>Total weight loss: 18 lbs</li>
                          <li>Waist circumference: 38 inches</li>
                          <li>Blood pressure normalized: 118/76</li>
                          <li><strong>Action:</strong> Continue testosterone, discontinue metformin, maintain lifestyle changes</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="ml-20 relative">
                      <div className="absolute -left-20 rounded-full bg-blue-500 text-white w-8 h-8 flex items-center justify-center">6m</div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <h6 className="font-medium">6-Month Follow-up</h6>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Total Testosterone: 850 ng/dL</li>
                          <li>Free Testosterone: 22 ng/dL</li>
                          <li>SHBG: 28 nmol/L (normalized)</li>
                          <li>Estradiol: 32 pg/mL</li>
                          <li>HOMA-IR: 1.8 (normal)</li>
                          <li>HbA1c: 5.3%</li>
                          <li>Total weight loss: 32 lbs (now BMI 26)</li>
                          <li><strong>Action:</strong> Adjust testosterone to 70mg twice weekly due to optimal levels</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 bg-emerald-50 p-4 rounded-md">
                  <h5 className="font-medium mb-2">Clinical Insights</h5>
                  <p className="text-sm">
                    This case demonstrates the powerful synergy between optimized testosterone and metabolic health interventions. The patient's initial low-normal testosterone was likely both a cause and consequence of his metabolic dysfunction. The combination of testosterone optimization with targeted metabolic interventions created a positive feedback loop, with improvements in body composition enhancing testosterone effectiveness and reducing insulin resistance.
                  </p>
                  <p className="text-sm mt-2">
                    <strong>Key Learning Points:</strong> 1) Low SHBG can mask true testosterone deficiency by maintaining adequate free testosterone despite low total levels; 2) Insulin resistance should be addressed concurrently with testosterone therapy; 3) Dose requirements may decrease as metabolic health improves.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mb-10">
              <h3 className="text-xl font-semibold mb-4">Case 2: Fertility Preservation</h3>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-2">Patient Profile</h4>
                    <ul className="list-disc pl-5 space-y-1 mb-4">
                      <li><span className="font-medium">Age:</span> 36</li>
                      <li><span className="font-medium">Chief Complaints:</span> Low energy, decreased libido, poor recovery from exercise</li>
                      <li><span className="font-medium">Medical History:</span> Unremarkable</li>
                      <li><span className="font-medium">Family Status:</span> Married, planning to have children in 1-2 years</li>
                      <li><span className="font-medium">Initial Lab Values:</span></li>
                      <ul className="list-circle pl-5 space-y-1 mt-1">
                        <li>Total Testosterone: 280 ng/dL</li>
                        <li>Free Testosterone: 5.8 ng/dL</li>
                        <li>LH: 1.2 mIU/mL (low)</li>
                        <li>FSH: 1.8 mIU/mL (low)</li>
                        <li>Estradiol: 18 pg/mL</li>
                        <li>Sperm count: 22 million/mL (within normal range)</li>
                      </ul>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Assessment and Plan</h4>
                    <p className="mb-3 text-sm">
                      This patient presents with secondary hypogonadism (low T with low gonadotropins) and desires symptom relief while maintaining fertility potential.
                    </p>
                    <div className="bg-blue-50 p-3 rounded-md mb-3">
                      <h6 className="font-medium text-sm">Treatment Protocol:</h6>
                      <ol className="list-decimal pl-5 text-sm space-y-1">
                        <li>hCG 750 IU three times weekly (M/W/F)</li>
                        <li>Clomiphene citrate 25mg three times weekly (opposite days from hCG)</li>
                        <li>Comprehensive men's health panel repeated at 6 weeks</li>
                        <li>Semen analysis at 3 months to confirm maintained parameters</li>
                        <li>Supplementation: Zinc 30mg, CoQ10 300mg, Mixed tocopherols 400 IU</li>
                      </ol>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <h4 className="font-semibold mb-3">Outcome Timeline</h4>
                  <div className="relative">
                    <div className="absolute h-full w-0.5 bg-blue-200 left-16 ml-px"></div>
                    
                    <div className="ml-20 mb-6 relative">
                      <div className="absolute -left-20 rounded-full bg-blue-500 text-white w-8 h-8 flex items-center justify-center">6w</div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <h6 className="font-medium">6-Week Follow-up</h6>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Total Testosterone: 620 ng/dL</li>
                          <li>Free Testosterone: 15.2 ng/dL</li>
                          <li>LH: 4.8 mIU/mL</li>
                          <li>FSH: 5.2 mIU/mL</li>
                          <li>Estradiol: 28 pg/mL</li>
                          <li>Improved energy and libido</li>
                          <li><strong>Action:</strong> Continue protocol, reduce clomiphene to 12.5mg three times weekly</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="ml-20 mb-6 relative">
                      <div className="absolute -left-20 rounded-full bg-blue-500 text-white w-8 h-8 flex items-center justify-center">3m</div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <h6 className="font-medium">3-Month Follow-up</h6>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Total Testosterone: 680 ng/dL</li>
                          <li>Free Testosterone: 16.8 ng/dL</li>
                          <li>Sperm count: 28 million/mL (improved)</li>
                          <li>Sperm motility: 58% (improved)</li>
                          <li>Maintaining normal testicular volume</li>
                          <li>Resolution of most hypogonadal symptoms</li>
                          <li><strong>Action:</strong> Continue protocol, annual semen analysis</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="ml-20 relative">
                      <div className="absolute -left-20 rounded-full bg-blue-500 text-white w-8 h-8 flex items-center justify-center">1y</div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <h6 className="font-medium">1-Year Follow-up</h6>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Total Testosterone: 720 ng/dL</li>
                          <li>Free Testosterone: 18.2 ng/dL</li>
                          <li>Sperm parameters maintained at optimal levels</li>
                          <li>Patient's wife conceived naturally after 3 months of trying</li>
                          <li>Complete resolution of hypogonadal symptoms</li>
                          <li><strong>Discussion:</strong> Options for transitioning to conventional TRT after completion of family planning vs. maintaining current protocol</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 bg-emerald-50 p-4 rounded-md">
                  <h5 className="font-medium mb-2">Clinical Insights</h5>
                  <p className="text-sm">
                    This case demonstrates successful management of hypogonadism while maintaining and even improving fertility parameters. By using a combination of hCG to mimic LH and stimulate intratesticular testosterone production, along with clomiphene to enhance pituitary stimulation of the testes, we achieved both symptom resolution and fertility preservation.
                  </p>
                  <p className="text-sm mt-2">
                    <strong>Key Learning Points:</strong> 1) Secondary hypogonadism often responds well to stimulatory protocols; 2) Combined hCG/SERM protocols can be maintained long-term with monitoring; 3) Fertility-sparing approaches often require patient education about the tradeoffs compared to traditional TRT.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mb-10">
              <h3 className="text-xl font-semibold mb-4">Case 3: Complex Hormone Management</h3>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-2">Patient Profile</h4>
                    <ul className="list-disc pl-5 space-y-1 mb-4">
                      <li><span className="font-medium">Age:</span> 52</li>
                      <li><span className="font-medium">Chief Complaints:</span> Fatigue, muscle loss, erectile dysfunction, mood disturbances</li>
                      <li><span className="font-medium">Medical History:</span> Well-controlled hypertension, elevated PSA (3.8 ng/mL) with negative biopsy 2 years ago</li>
                      <li><span className="font-medium">Current Medications:</span> Lisinopril 10mg</li>
                      <li><span className="font-medium">Initial Lab Values:</span></li>
                      <ul className="list-circle pl-5 space-y-1 mt-1">
                        <li>Total Testosterone: 310 ng/dL</li>
                        <li>Free Testosterone: 6.5 ng/dL</li>
                        <li>SHBG: 52 nmol/L (elevated)</li>
                        <li>Estradiol: 12 pg/mL (low)</li>
                        <li>DHT: 25 ng/dL (low)</li>
                        <li>Thyroid: TSH 4.2 mIU/L, Free T3 2.4 pg/mL (low-normal)</li>
                        <li>PSA: 3.6 ng/mL</li>
                      </ul>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Assessment and Plan</h4>
                    <p className="mb-3 text-sm">
                      This patient presents with complex hormonal dysregulation: low testosterone with high SHBG (reducing free hormone availability), low estradiol, and subclinical hypothyroidism, all against a background of PSA monitoring.
                    </p>
                    <div className="bg-blue-50 p-3 rounded-md mb-3">
                      <h6 className="font-medium text-sm">Treatment Protocol:</h6>
                      <ol className="list-decimal pl-5 text-sm space-y-1">
                        <li>Testosterone cypionate 80mg twice weekly</li>
                        <li>Low-dose levothyroxine (25mcg daily) for subclinical hypothyroidism</li>
                        <li>No AI initially given low baseline estradiol</li>
                        <li>Boron 9mg daily to help reduce SHBG</li>
                        <li>Comprehensive metabolic support:</li>
                        <ul className="list-disc pl-5 mt-1">
                          <li>Vitamin D 5000 IU</li>
                          <li>Magnesium glycinate 300mg</li>
                          <li>Omega-3 2g EPA/DHA</li>
                        </ul>
                        <li>Close PSA monitoring at 6 weeks, 3 months, and quarterly</li>
                      </ol>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <h4 className="font-semibold mb-3">Outcome Timeline</h4>
                  <div className="relative">
                    <div className="absolute h-full w-0.5 bg-blue-200 left-16 ml-px"></div>
                    
                    <div className="ml-20 mb-6 relative">
                      <div className="absolute -left-20 rounded-full bg-blue-500 text-white w-8 h-8 flex items-center justify-center">6w</div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <h6 className="font-medium">6-Week Follow-up</h6>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Total Testosterone: 950 ng/dL</li>
                          <li>Free Testosterone: 22 ng/dL</li>
                          <li>SHBG: 48 nmol/L (slightly improved)</li>
                          <li>Estradiol: 42 pg/mL (now optimal)</li>
                          <li>PSA: 3.8 ng/mL (slight increase)</li>
                          <li>TSH: 2.8 mIU/L (improved)</li>
                          <li>Improved energy and libido</li>
                          <li><strong>Action:</strong> Continue protocol, monitor PSA closely</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="ml-20 mb-6 relative">
                      <div className="absolute -left-20 rounded-full bg-blue-500 text-white w-8 h-8 flex items-center justify-center">3m</div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <h6 className="font-medium">3-Month Follow-up</h6>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Total Testosterone: 880 ng/dL</li>
                          <li>Free Testosterone: 23 ng/dL</li>
                          <li>SHBG: 42 nmol/L (continuing to improve)</li>
                          <li>Estradiol: 48 pg/mL (slightly elevated)</li>
                          <li>PSA: 4.0 ng/mL</li>
                          <li>Hematocrit: 52%</li>
                          <li><strong>Action:</strong> Add low-dose anastrozole 0.25mg once weekly, reduce T dose to 70mg twice weekly, urological consultation for PSA</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="ml-20 mb-6 relative">
                      <div className="absolute -left-20 rounded-full bg-blue-500 text-white w-8 h-8 flex items-center justify-center">6m</div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <h6 className="font-medium">6-Month Follow-up</h6>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Total Testosterone: 740 ng/dL</li>
                          <li>Free Testosterone: 18 ng/dL</li>
                          <li>Estradiol: 32 pg/mL (optimized)</li>
                          <li>PSA: 3.7 ng/mL (stabilized)</li>
                          <li>Hematocrit: 49%</li>
                          <li>Urology: Repeat MRI negative, continued surveillance</li>
                          <li><strong>Action:</strong> Continue with current protocol</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="ml-20 relative">
                      <div className="absolute -left-20 rounded-full bg-blue-500 text-white w-8 h-8 flex items-center justify-center">1y</div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <h6 className="font-medium">1-Year Follow-up</h6>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          <li>Total Testosterone: 720 ng/dL</li>
                          <li>Free Testosterone: 19 ng/dL</li>
                          <li>SHBG: 38 nmol/L (normalized)</li>
                          <li>Estradiol: 30 pg/mL</li>
                          <li>PSA: 3.5 ng/mL (stable)</li>
                          <li>Complete resolution of symptoms</li>
                          <li>10 lbs muscle gain, 8 lbs fat loss</li>
                          <li><strong>Action:</strong> Continue protocol with quarterly monitoring</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 bg-emerald-50 p-4 rounded-md">
                  <h5 className="font-medium mb-2">Clinical Insights</h5>
                  <p className="text-sm">
                    This complex case illustrates several important principles of advanced hormone management: 1) The need to address multiple hormonal systems simultaneously (testosterone, thyroid, estradiol); 2) The importance of managing SHBG levels to optimize free hormone availability; 3) The strategy for monitoring PSA during testosterone therapy in men with elevated baseline levels.
                  </p>
                  <p className="text-sm mt-2">
                    <strong>Key Learning Points:</strong> Testosterone therapy did cause a transient rise in PSA, but it stabilized and eventually declined slightly with optimization of the hormone profile. The temporary elevation initially required close monitoring rather than discontinuation. Additionally, the high SHBG likely contributed to the patient's symptoms despite "low-normal" total testosterone, highlighting the importance of assessing free hormone levels and their bioavailability.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-10">
              <h3 className="text-xl font-semibold mb-4">Clinical Pearls from Experienced Practitioners</h3>
              
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="bg-indigo-50 border-indigo-200">
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-2">Optimizing Injection Technique</h4>
                    <p className="text-sm mb-3">
                      "Subcutaneous testosterone administration offers similar efficacy to IM with less pain and fewer peaks and troughs. I've transitioned 80% of my patients to SubQ with improved satisfaction."
                    </p>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>Use 27-30g insulin syringes for minimal pain</li>
                      <li>Rotate sites between abdomen and lateral thigh</li>
                      <li>Inject slowly (10 seconds per 0.5mL)</li>
                      <li>Consider more frequent, lower volume injections</li>
                    </ul>
                    <p className="text-sm mt-3 italic">
                      - Clinical Expert in Hormone Optimization
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="bg-indigo-50 border-indigo-200">
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-2">The Estradiol Balance</h4>
                    <p className="text-sm mb-3">
                      "The most common mistake I see in TRT practice is aggressive estrogen suppression. In men, estradiol is not the enemy—it's essential for joint health, cognitive function, and cardiovascular protection."
                    </p>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>Only treat high estradiol if symptomatically problematic</li>
                      <li>Target E2 levels between 20-35 pg/mL for most men</li>
                      <li>Consider the testosterone:estradiol ratio (ideally 15-25:1)</li>
                      <li>Low estradiol (&lt;15 pg/mL) often causes worse symptoms than high</li>
                    </ul>
                    <p className="text-sm mt-3 italic">
                      - Endocrinology Specialist
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="bg-indigo-50 border-indigo-200">
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-2">Beyond Numbers</h4>
                    <p className="text-sm mb-3">
                      "Laboratory values are only part of the story. The most successful protocols are individualized based on symptom response, not just targeting specific numbers."
                    </p>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>Track subjective improvements with validated questionnaires</li>
                      <li>Some patients feel optimal at T levels of 600 ng/dL, others at 900</li>
                      <li>Monitor changes in body composition, not just lab values</li>
                      <li>Adjust protocol based on a combination of labs and clinical response</li>
                      <li>Consider genetic factors (androgen receptor sensitivity varies)</li>
                    </ul>
                    <p className="text-sm mt-3 italic">
                      - Precision Health Practitioner
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        </TabsContent>
      </Tabs>

      <div className="text-center mt-12 mb-4">
        <p className="text-sm text-gray-600">
          This clinical training content was developed for educational purposes for longevity medicine practitioners.
        </p>
        <p className="text-sm text-gray-600 mt-1">
          Last updated: April 2025 | Contact for additional clinical resources
        </p>
      </div>
    </div>
    </>
  );
};

export const MaleHRTExample = MaleHRTContent;